import { v4 as uuidv4 } from "uuid";

import { createTemporaryS3Credentials } from "../api";
import { CDN_URL, S3_BUCKET } from "../constants";

import { triggerDownload } from "./download";
import { createNewZip } from "./jszip";
import { canDownload, openLink } from "./platform";
import { getFileExtensionFromPath } from "./string";

let s3Module;

// returns png as default.
function getImageExtension(url) {
    const match = url.match(/\.(png|jpe?g|gif|webp|svg)(\?.*)?$/i);

    return match ? match[1].toLowerCase() : "png";
}

async function getS3Module() {
    if (s3Module) {
        return s3Module;
    }

    s3Module = (await import(/* webpackChunkName: "aws-sdk-s3client", webpackPrefetch: true */"@aws-sdk/client-s3"));
    return s3Module;
}

const mimeTypes = Object.freeze({
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    js: "application/javascript",
    javascript: "application/javascript",
    pdf: "application/pdf",
    png: "image/png",
    svg: "image/svg+xml",
    xml: "application/xml",
    webp: "image/webp",
    gif: "image/gif"
});

const imageMimeTypes = ["image/gif", "image/svg+xml", "image/png", "image/jpeg"];

async function exportCode({
    resourceType,
    barrelName,
    exportObjects
}) {
    if (Array.isArray(exportObjects)) {
        const zipFile = await createNewZip();

        exportObjects.forEach(exportObj => {
            zipFile.file(exportObj.filename, exportObj.code);
        });

        const blob = await zipFile.generateAsync({ type: "blob" });
        triggerDownload({
            data: URL.createObjectURL(blob),
            name: `${resourceType}s_${barrelName}.zip`
        });
    } else {
        triggerDownload({
            data: URL.createObjectURL(new Blob([exportObjects.code], { type: mimeTypes[exportObjects.language] })),
            name: exportObjects.filename
        });
    }
}

async function prepareScreen(screen, versionId) {
    const { name, versions } = screen;
    const screenVersion = versionId
        ? versions.find(({ _id }) => _id === versionId)
        : versions[0];
    const { snapshot: { url } } = screenVersion;
    const res = await fetch(url);
    const data = await res.arrayBuffer();
    const extension = getImageExtension(url);

    return {
        name: `${name}.${extension}`,
        blob: new Blob([data], { type: mimeTypes[extension] })
    };
}

async function downloadScreens(screens, fileName) {
    if (screens.length === 1) {
        await donwloadSnapshotVersion(screens[0]);

        return;
    }

    const screenZip = await createNewZip();
    const preparedScreens = await Promise.all(screens.map(screen => prepareScreen(screen)));
    const usedScreenNameCounts = {};

    preparedScreens.forEach(({ name, blob }) => {
        let screenName;

        if (usedScreenNameCounts[name] > 0) {
            screenName = name.replace(/(.*)\.(.*$)/, `$1 (${usedScreenNameCounts[name]++}).$2`);
        } else {
            usedScreenNameCounts[name] = 1;
            screenName = name;
        }

        screenZip.file(screenName, blob);
    });

    const blob = await screenZip.generateAsync({ type: "blob" });

    triggerDownload({
        data: URL.createObjectURL(blob),
        name: fileName
    });
}

async function donwloadSnapshotVersion(screen, versionId) {
    if (!canDownload()) {
        let { snapshot: { url } } = versionId
            ? screen.versions.find(({ _id }) => _id === versionId)
            : screen.versions[0];

        if (!url.includes("raven-warehouse")) {
            const { pathname } = new URL(url);
            url = CDN_URL + pathname;
        }

        openLink(url);
        return;
    }

    const { name, blob } = await prepareScreen(screen, versionId);

    triggerDownload({
        data: URL.createObjectURL(blob),
        name
    });
}

async function uploadCommentImage(file, abortSignal) {
    const fileName = `${uuidv4()}.${getFileExtensionFromPath(file.name)}`;
    const fileKey = `users/${Zeplin.user._id}/contents/${fileName}`;

    const [{ expiration: _expiration, ...credentials }, S3] = await Promise.all([
        createTemporaryS3Credentials(),
        getS3Module()
    ]);

    const { S3Client, PutObjectCommand } = S3;

    const s3Client = new S3Client({
        region: "us-west-1",
        credentials
    });

    const command = new PutObjectCommand({
        Bucket: S3_BUCKET,
        Key: fileKey,
        ContentType: file.type,
        Body: file
    });

    await s3Client.send(command, { abortSignal });

    return {
        fileURL: `${CDN_URL}/${fileKey}`
    };
}

export {
    mimeTypes as MIME_TYPE,
    imageMimeTypes as IMAGE_MIME_TYPES,
    exportCode,
    downloadScreens,
    donwloadSnapshotVersion,
    uploadCommentImage
};
